import { useMutation } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { getCombinedRecommendation } from "../utils/soilRecommendations";
import { getRadiusForLocation } from "../constants/gridPointDistanceConfig";
import { useTranslation } from "react-i18next";

// ─────────────────────────────────────────────
// useSoilData
// Fetches soil data from Supabase using either:
// 1. Exact sample data (if user clicked on a known location)
// 2. Estimated data (if we had to blend nearby samples)
// 3. No data (if no samples exist within the radius)
//
// The hook clearly distinguishes between these cases
// so the UI can show appropriate messages.
// ─────────────────────────────────────────────

const useSoilData = () => {
  const { t } = useTranslation();

  const {
    mutateAsync: fetchSoilData,
    data: soilResult,
    isPending: isLoadingSoilData,
    error,
    reset,
  } = useMutation({
    mutationFn: async ({ lat, lng, locationName }) => {
      try {
        // Determine the radius based on which state the location is in
        // Kano: 7km, Kaduna: 11km, default: 9km
        const radius = getRadiusForLocation(locationName);

        // Call the IDW function to get soil data
        const { data, error: supabaseError } = await supabase.rpc(
          "get_interpolated_soil_data_idw",
          {
            input_lat: lat,
            input_lng: lng,
            max_distance_meters: radius,
          },
        );

        if (supabaseError) throw supabaseError;

        // ── Check if data was returned ──
        if (!data || data.length === 0) {
          throw new Error(t("soil.noSoilDataFound"));
        }

        const row = data?.[0];

        // ── Check if the SQL function returned "No Data" ──
        // This happens when no samples exist within the search radius.
        // The SQL function returns a row with NULL values and
        // interpretation = "No Data" instead of throwing an error.
        if (row.interpretation === "No Data") {
          throw new Error(t("soil.noSoilDataFound"));
        }

        // ── Check if all nutrient values are NULL ──
        // This is a safety check in case something unexpected happens
        if (
          row.interpolated_ph === null &&
          row.interpolated_soc === null &&
          row.interpolated_nitrogen === null &&
          row.interpolated_phosphorus === null &&
          row.interpolated_potassium === null
        ) {
          // This means the function returned a row but all values are NULL, which shouldn't happen
          throw new Error(t("soil.unableToRetrieveSoilData"));
        }

        // ── Process the valid soil data ──
        // Calculate recommendations using the interpolated or exact values
        const recommendation = getCombinedRecommendation(
          row.interpolated_ph,
          row.interpolated_soc,
          row.interpolated_nitrogen,
          row.interpolated_phosphorus,
          row.interpolated_potassium,
        );

        // Return both the soil data and clear metadata
        return {
          soilData: {
            avg_ph: row.interpolated_ph,
            avg_soc: row.interpolated_soc,
            avg_nitrogen: row.interpolated_nitrogen,
            avg_phosphorus: row.interpolated_phosphorus,
            avg_potassium: row.interpolated_potassium,

            // ── Metadata about data quality ──
            // is_exact_match = TRUE if user clicked directly on a known sample location
            // is_exact_match = FALSE if we estimated using nearby points
            is_exact_match: row.is_exact_match,

            // These are only meaningful if is_exact_match = FALSE (estimated data)
            confidence_score: row.confidence_score,
            interpretation: row.interpretation,
            points_used: row.points_used,
          },
          recommendation,
        };
      } catch (err) {
        // If the error is already an instance of Error, re-throw it to preserve the message
        if (err instanceof Error) {
          throw err;
        }

        // Catch any unexpected errors and throw a generic message
        throw new Error(t("soil.unableToFetchSoilData"));
      }
    },
  });

  return {
    fetchSoilData,
    soilData: soilResult?.soilData ?? null,
    recommendation: soilResult?.recommendation ?? {
      ph: {},
      soc: {},
      nitrogen: {},
      phosphorus: {},
      potassium: {},
    },
    // ── Data quality flags ──
    // These help the UI decide what to show the user
    isExactMatch: soilResult?.soilData?.is_exact_match ?? false,
    confidenceScore: soilResult?.soilData?.confidence_score ?? null,
    confidenceInterpretation: soilResult?.soilData?.interpretation ?? null,
    pointsUsed: soilResult?.soilData?.points_used ?? null,

    isLoadingSoilData,
    soilDataError: error?.message ?? null,
    reset,
  };
};

export default useSoilData;
